import { Navigate } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import ErrorPage from '../components/Errors/ErrorPage';
import PageNotFound from '../components/Errors/PageNotFound';
import LandingPage from '../components/LandingPage/LandingPage';
import FitnessPage from '../components/JoinOurNetwork/FitnessPage';
import RecruitmentPage from '../components/Recruitment/RecruitmentPage';
import ResourceGroupListPage from '../components/Resources/ResourceGroupListPage';
import ResourceGroupPage from '../components/Resources/ResourceGroupPage';
import ResourcePage from '../components/Resources/ResourcePage';
import FaqsPage from '../components/Faqs/FaqsPage';
import JONGroupListPage from '../components/JoinOurNetwork/Providers/JoinOurNetworkGroupListPage';
import JONGroupPage from '../components/JoinOurNetwork/Providers/JoinOurNetworkGroupPage';
import Layout from '../components/shared/Layout';
import NetworkSearchLayout from '../components/NetworkSearch/NetworkSearchLayout';
import {
	AREA_ROUTES,
	NETWORK_SEARCH_ROUTES,
	TOP_LEVEL_ROUTES,
} from '../scripts/routes';
import { HOME_URL } from '../scripts/navigationItems';
import StaticContentSkeleton from '../components/StaticContentPage/StaticContentSkeleton';
import CignaSSOError from '../components/CignaSSO/CignaSSOError';
import Search from '../components/NetworkSearch/Search';
import Result from '../components/NetworkSearch/Result';
import LanguageServices from '../components/NetworkSearch/LanguageAccessServices';

const StaticContentPage = lazy(
	() => import('../components/StaticContentPage/StaticContentPage')
);

export const unauthorizedCallback = () => {
	const encodedUrl = encodeURIComponent(window.location.href);
	window.location.href = `/identity/login?redirect=${encodedUrl}`;
};

export const postLoginUnauthorizedCallback = () => {
	window.location.href = `/ASH/default.aspx`;
};

export const useRoutesList = () => [
	{
		path: HOME_URL,
		element: <Layout />,
		children: [
			{
				index: true,
				element: <LandingPage />,
			},
			{
				path: `${TOP_LEVEL_ROUTES.recruitment}/:regionSpecialtyString`,
				element: <RecruitmentPage />,
			},
			{
				path: TOP_LEVEL_ROUTES.features,
				element: <PageNotFound />,
			},
			{
				path: TOP_LEVEL_ROUTES.resources,
				children: [
					{
						path: AREA_ROUTES.providers,
						element: <ResourceGroupListPage />,
					},
					{
						path: ':groupPageId',
						element: <ResourceGroupPage />,
					},
					{
						path: ':groupPageId/faq',
						element: <FaqsPage />,
					},
					{
						path: ':groupPageId/:pageId',
						element: <ResourcePage />,
					},
				],
			},
			{
				path: TOP_LEVEL_ROUTES.joinOurNetwork,
				children: [
					{
						path: AREA_ROUTES.providers,
						element: <JONGroupListPage />,
					},
					{
						path: AREA_ROUTES.fitnessCenters,
						element: <FitnessPage />,
					},
					{
						path: ':groupPageId',
						element: <JONGroupPage />,
					},
				],
			},
			{
				path: TOP_LEVEL_ROUTES.termsAndConditions,
				element: (
					<Suspense fallback={<StaticContentSkeleton />}>
						<StaticContentPage />
					</Suspense>
				),
			},
			{
				path: TOP_LEVEL_ROUTES.privacy,
				element: (
					<Suspense fallback={<StaticContentSkeleton />}>
						<StaticContentPage type='PrivacyStatement' />
					</Suspense>
				),
			},
			{
				path: TOP_LEVEL_ROUTES.error,
				element: <ErrorPage />,
			},
			{
				path: TOP_LEVEL_ROUTES.pageNotFound,
				element: <PageNotFound />,
			},
			{
				path: '/cigna-sso-error',
				element: <CignaSSOError />,
				handle: { hideHeader: true },
			},
			{
				path: '*',
				element: <Navigate to={`/${TOP_LEVEL_ROUTES.pageNotFound}`} />,
			},
		],
	},
	{
		path: NETWORK_SEARCH_ROUTES.root,
		element: <NetworkSearchLayout />,
		children: [
			{
				index: true,
				element: <Search />,
			},
			{
				path: 'languageservices',
				element: <LanguageServices />,
			},
			{
				path: 'languageservices/:language',
				element: <LanguageServices />,
			},
			{
				path: ':healthplanCode',
				children: [
					{
						index: true,
						element: <Search />,
					},
					{
						path: NETWORK_SEARCH_ROUTES.results,
						element: <Result />,
						handle: { fullWidth: true },
					},
				],
			},
		],
	},
];
